import {
  initPublicAuth,
  loginWithEmail,
  registerWithEmail,
  loginWithGoogle,
  logoutPublic,
  requestPasswordReset,
  formatAuthErrorSafe,
} from "../services/publicAuthService";
import { getGuestDisplayName, setGuestDisplayName } from "../services/guestProfile";

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;");

/**
 * @param {HTMLElement} container
 * @param {{ user: import('firebase/auth').User | null; ready: boolean; error: string | null; isAnonymous?: boolean }} state
 */
export const renderPublicAuthPanel = (container, state) => {
  if (!container) return;

  const { user, ready, error, isAnonymous } = state;
  const showLoggedIn = ready && user && !isAnonymous;
  const sessionError = ready && !user && error;
  const label = user?.email || user?.displayName || (isAnonymous ? "Invitado" : "");

  container.innerHTML = `
    <div class="public-auth" data-public-auth>
      ${
        !ready
          ? `<span class="public-auth__status" aria-live="polite">Conectando…</span>`
          : sessionError
            ? `<span class="public-auth__banner-error" role="alert">${escapeHtml(error)}</span>`
            : showLoggedIn
            ? `<div class="public-auth__session">
                 <span class="public-auth__email" title="${escapeHtml(label)}">
                   <i class="fa-solid fa-user-check public-auth__email-icon" aria-hidden="true"></i>
                   <span class="public-auth__email-text">${escapeHtml(label)}</span>
                 </span>
                 <a href="perfil.html" class="btn btn--ghost public-auth__btn public-auth__btn--profile" aria-label="Mi perfil">
                   <i class="fa-solid fa-id-card" aria-hidden="true"></i>
                   <span class="public-auth__profile-text">Mi perfil</span>
                 </a>
                 <button type="button" class="btn btn--ghost public-auth__btn public-auth__btn--logout" data-auth-logout aria-label="Cerrar sesión">
                   <i class="fa-solid fa-right-from-bracket" aria-hidden="true"></i>
                   <span class="public-auth__logout-text">Salir</span>
                 </button>
               </div>`
            : `<button type="button" class="btn btn--ghost public-auth__btn" data-auth-open><i class="fa-regular fa-circle-user" aria-hidden="true"></i> Cuenta</button>`
      }
    </div>
    ${
      ready && user?.isAnonymous
        ? `<dialog class="public-auth__dialog" data-auth-dialog aria-labelledby="public-auth-title">
      <div class="public-auth__dialog-inner">
        <button type="button" class="public-auth__close" data-auth-close aria-label="Cerrar">&times;</button>
        <h2 id="public-auth-title" class="public-auth__title">Tu cuenta</h2>
        <p class="public-auth__hint">Podés continuar como visitante, usar Google o email.</p>
        <p class="public-auth__error" data-auth-form-error role="alert" hidden></p>
        <div class="public-auth__guest-block">
          <label class="public-auth__guest-label">
            <span class="public-auth__label-text"><i class="fa-solid fa-id-card" aria-hidden="true"></i> Nombre para pedidos (opcional)</span>
            <input type="text" data-guest-name-input maxlength="80" placeholder="Ej. Faki" autocomplete="name" value="${escapeHtml(getGuestDisplayName())}" />
          </label>
          <button type="button" class="btn btn--ghost public-auth__guest-btn" data-auth-guest>
            <i class="fa-solid fa-person-walking" aria-hidden="true"></i>
            <span>Continuar como visitante</span>
          </button>
        </div>
        <div class="public-auth__divider">o con email</div>
        <div class="public-auth__tabs" role="tablist">
          <button type="button" class="public-auth__tab is-active" data-tab="login" role="tab" aria-selected="true"><i class="fa-solid fa-right-to-bracket" aria-hidden="true"></i> Entrar</button>
          <button type="button" class="public-auth__tab" data-tab="register" role="tab" aria-selected="false"><i class="fa-solid fa-user-plus" aria-hidden="true"></i> Registro</button>
        </div>
        <div data-auth-login-block>
          <form class="public-auth__form" data-auth-form-login data-panel="login">
            <label><span class="public-auth__label-text"><i class="fa-solid fa-envelope" aria-hidden="true"></i> Email</span><input type="email" name="email" required autocomplete="email" /></label>
            <label><span class="public-auth__label-text"><i class="fa-solid fa-lock" aria-hidden="true"></i> Contraseña</span><input type="password" name="password" required autocomplete="current-password" /></label>
            <button type="submit" class="btn btn--primary public-auth__submit"><i class="fa-solid fa-right-to-bracket" aria-hidden="true"></i> Entrar</button>
          </form>
          <p class="public-auth__forgot">
            <button type="button" class="public-auth__link" data-auth-show-reset>Olvidé mi contraseña</button>
          </p>
        </div>
        <div class="public-auth__reset-panel is-hidden" data-auth-reset-panel hidden>
          <p class="public-auth__hint">Ingresá el correo de tu cuenta. Te enviaremos un enlace para elegir una nueva contraseña.</p>
          <label><span class="public-auth__label-text"><i class="fa-solid fa-envelope" aria-hidden="true"></i> Email</span><input type="email" name="reset-email" autocomplete="email" data-auth-reset-email /></label>
          <button type="button" class="btn btn--primary public-auth__submit" data-auth-send-reset><i class="fa-solid fa-paper-plane" aria-hidden="true"></i> Enviar enlace</button>
          <button type="button" class="btn btn--ghost public-auth__submit" data-auth-hide-reset>Volver a iniciar sesión</button>
          <p class="public-auth__success" data-auth-reset-success hidden role="status"></p>
        </div>
        <form class="public-auth__form is-hidden" data-auth-form-register data-panel="register" hidden>
          <label><span class="public-auth__label-text"><i class="fa-solid fa-signature" aria-hidden="true"></i> Nombre (opcional)</span><input type="text" name="name" autocomplete="name" /></label>
          <label><span class="public-auth__label-text"><i class="fa-solid fa-envelope" aria-hidden="true"></i> Email</span><input type="email" name="email" required autocomplete="email" /></label>
          <label><span class="public-auth__label-text"><i class="fa-solid fa-lock" aria-hidden="true"></i> Contraseña</span><input type="password" name="password" required minlength="6" autocomplete="new-password" /></label>
          <button type="submit" class="btn btn--primary public-auth__submit"><i class="fa-solid fa-user-plus" aria-hidden="true"></i> Crear cuenta</button>
        </form>
        <div class="public-auth__divider">o</div>
        <button type="button" class="btn btn--secondary public-auth__google" data-auth-google>
          <i class="fa-brands fa-google" aria-hidden="true"></i>
          <span>Continuar con Google</span>
        </button>
      </div>
    </dialog>`
        : ""
    }
  `;

  const dialog = container.querySelector("[data-auth-dialog]");
  const openBtn = container.querySelector("[data-auth-open]");
  const closeBtn = container.querySelector("[data-auth-close]");
  const formError = container.querySelector("[data-auth-form-error]");
  const formLogin = container.querySelector("[data-auth-form-login]");
  const formRegister = container.querySelector("[data-auth-form-register]");
  const loginBlock = container.querySelector("[data-auth-login-block]");
  const resetPanel = container.querySelector("[data-auth-reset-panel]");
  const resetEmailInput = container.querySelector("[data-auth-reset-email]");
  const resetSuccess = container.querySelector("[data-auth-reset-success]");
  const showResetBtn = container.querySelector("[data-auth-show-reset]");
  const sendResetBtn = container.querySelector("[data-auth-send-reset]");
  const hideResetBtn = container.querySelector("[data-auth-hide-reset]");
  const tabLogin = container.querySelector('[data-tab="login"]');
  const tabRegister = container.querySelector('[data-tab="register"]');
  const googleBtn = container.querySelector("[data-auth-google]");
  const guestBtn = container.querySelector("[data-auth-guest]");
  const guestNameInput = container.querySelector("[data-guest-name-input]");
  const logoutBtn = container.querySelector("[data-auth-logout]");

  const hideResetPanel = () => {
    resetPanel?.classList.add("is-hidden");
    resetPanel?.setAttribute("hidden", "");
    loginBlock?.classList.remove("is-hidden");
    loginBlock?.removeAttribute("hidden");
    if (resetSuccess) {
      resetSuccess.textContent = "";
      resetSuccess.hidden = true;
    }
    if (resetEmailInput) resetEmailInput.value = "";
  };

  const showResetPanel = () => {
    resetPanel?.classList.remove("is-hidden");
    resetPanel?.removeAttribute("hidden");
    loginBlock?.classList.add("is-hidden");
    loginBlock?.setAttribute("hidden", "");
    const loginEmail = formLogin?.querySelector('input[name="email"]')?.value;
    if (resetEmailInput && loginEmail) resetEmailInput.value = loginEmail.trim();
    showError("");
  };

  const showError = (msg) => {
    if (!formError) return;
    if (msg) {
      formError.textContent = msg;
      formError.hidden = false;
    } else {
      formError.textContent = "";
      formError.hidden = true;
    }
  };

  const setBusy = (busy) => {
    if (dialog) {
      dialog.querySelectorAll("button, input").forEach((el) => {
        el.disabled = busy;
      });
    }
  };

  openBtn?.addEventListener("click", () => {
    showError("");
    hideResetPanel();
    dialog?.showModal();
  });

  closeBtn?.addEventListener("click", () => {
    dialog?.close();
  });

  dialog?.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });

  tabLogin?.addEventListener("click", () => {
    tabLogin.classList.add("is-active");
    tabRegister?.classList.remove("is-active");
    tabLogin.setAttribute("aria-selected", "true");
    tabRegister?.setAttribute("aria-selected", "false");
    loginBlock?.classList.remove("is-hidden");
    loginBlock?.removeAttribute("hidden");
    formLogin?.classList.remove("is-hidden");
    formRegister?.classList.add("is-hidden");
    formRegister?.setAttribute("hidden", "");
    formLogin?.removeAttribute("hidden");
    hideResetPanel();
    showError("");
  });

  tabRegister?.addEventListener("click", () => {
    tabRegister.classList.add("is-active");
    tabLogin?.classList.remove("is-active");
    tabRegister.setAttribute("aria-selected", "true");
    tabLogin?.setAttribute("aria-selected", "false");
    loginBlock?.classList.add("is-hidden");
    loginBlock?.setAttribute("hidden", "");
    resetPanel?.classList.add("is-hidden");
    resetPanel?.setAttribute("hidden", "");
    formRegister?.classList.remove("is-hidden");
    formLogin?.classList.add("is-hidden");
    formLogin?.setAttribute("hidden", "");
    formRegister?.removeAttribute("hidden");
    if (resetSuccess) {
      resetSuccess.textContent = "";
      resetSuccess.hidden = true;
    }
    if (resetEmailInput) resetEmailInput.value = "";
    showError("");
  });

  formLogin?.addEventListener("submit", async (e) => {
    e.preventDefault();
    showError("");
    const fd = new FormData(formLogin);
    const email = fd.get("email")?.toString().trim();
    const password = fd.get("password")?.toString();
    if (!email || !password) {
      showError("Completá email y contraseña.");
      return;
    }
    setBusy(true);
    try {
      await loginWithEmail(email, password);
      dialog?.close();
    } catch (err) {
      showError(formatAuthErrorSafe(err));
    } finally {
      setBusy(false);
    }
  });

  formRegister?.addEventListener("submit", async (e) => {
    e.preventDefault();
    showError("");
    const fd = new FormData(formRegister);
    const email = fd.get("email")?.toString().trim();
    const password = fd.get("password")?.toString();
    const name = fd.get("name")?.toString().trim();
    if (!email || !password) {
      showError("Completá email y contraseña.");
      return;
    }
    setBusy(true);
    try {
      await registerWithEmail(email, password, name || undefined);
      dialog?.close();
    } catch (err) {
      showError(formatAuthErrorSafe(err));
    } finally {
      setBusy(false);
    }
  });

  googleBtn?.addEventListener("click", async () => {
    showError("");
    setBusy(true);
    try {
      await loginWithGoogle();
      dialog?.close();
    } catch (err) {
      showError(formatAuthErrorSafe(err));
    } finally {
      setBusy(false);
    }
  });

  showResetBtn?.addEventListener("click", () => {
    showResetPanel();
  });

  hideResetBtn?.addEventListener("click", () => {
    hideResetPanel();
    showError("");
  });

  sendResetBtn?.addEventListener("click", async () => {
    showError("");
    if (resetSuccess) {
      resetSuccess.textContent = "";
      resetSuccess.hidden = true;
    }
    const email = resetEmailInput?.value?.trim();
    if (!email) {
      showError("Ingresá tu correo electrónico.");
      return;
    }
    setBusy(true);
    try {
      await requestPasswordReset(email);
      if (resetSuccess) {
        resetSuccess.textContent =
          "Si ese correo está registrado, recibirás un enlace para restablecer la contraseña. Revisá la bandeja de entrada y el spam.";
        resetSuccess.hidden = false;
      }
    } catch (err) {
      showError(formatAuthErrorSafe(err));
    } finally {
      setBusy(false);
    }
  });

  guestBtn?.addEventListener("click", () => {
    const name = guestNameInput?.value?.trim() || "";
    setGuestDisplayName(name);
    showError("");
    dialog?.close();
  });

  logoutBtn?.addEventListener("click", async () => {
    try {
      await logoutPublic();
    } catch (err) {
      console.warn("Logout:", err);
    }
  });
};

/**
 * Inicializa auth pública en el slot del header (si existe).
 * @param {string} [selector]
 * @param {(state: { user: import('firebase/auth').User | null; ready: boolean; error: string | null; isAnonymous?: boolean }) => void} [onState]
 */
export const mountPublicAuth = (selector = "#header-auth", onState) => {
  const root = document.querySelector(selector);
  if (!root) return () => {};
  return initPublicAuth((state) => {
    renderPublicAuthPanel(root, state);
    onState?.(state);
  });
};
